import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import env from './utils/env';

type VerificationStatus = 'verifying' | 'success' | 'error';

interface VerifyEmailProps {
    testStatus?: VerificationStatus;
    testError?: string;
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({ testStatus, testError }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [status, setStatus] = useState<VerificationStatus>('verifying');
    const [error, setError] = useState('');

    const apiUrl = env.apiUrl;

    useEffect(() => {
        // If this is a test mode, use the test status
        if (testStatus) {
            setStatus(testStatus);
            if (testError) {
                setError(testError);
            }
            return;
        }

        const verifyEmail = async () => {
            const token = searchParams.get('token');

            if (!token) {
                setError('Invalid verification link');
                setStatus('error');
                return;
            }

            try {
                const response = await fetch(`${apiUrl}/auth/verify-email`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Verification failed');
                }

                // Auto-login the user
                login(data.user, data.token);
                setStatus('success');

                // Redirect to home page after 3 seconds
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            } catch (err: any) {
                setError(err.message || 'Failed to verify email');
                setStatus('error');
            }
        };

        verifyEmail();
    }, [searchParams, navigate, login, apiUrl, testStatus, testError]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center'
        }}>
            {status === 'verifying' && (
                <div>
                    <h2>Verifying your email...</h2>
                    <p>Please wait while we verify your email address.</p>
                    <div style={{ marginTop: '20px' }}>
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '2em' }}></i>
                    </div>
                </div>
            )}

            {status === 'success' && (
                <div style={{ color: 'green' }}>
                    <h2>Email Verified Successfully!</h2>
                    <p>Your account has been upgraded to USER role.</p>
                    <p>Redirecting you to the home page...</p>
                </div>
            )}

            {status === 'error' && (
                <div style={{ color: 'red' }}>
                    <h2>Verification Failed</h2>
                    <p>{error}</p>
                    <button 
                        onClick={() => navigate('/login')}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Go to Login
                    </button>
                </div>
            )}
        </div>
    );
};

export default VerifyEmail; 